import type { ExportBatch, ExportFormat, ExportIssue } from "../types";

export type FormatOption = {
  id: ExportFormat;
  title: string;
  desc: string;
};

export const EXPORT_FORMATS: ReadonlyArray<FormatOption> = [
  {
    id: "ledes_1998b",
    title: "LEDES 1998B",
    desc: "Imports into most legal billing and e-billing systems. One invoice per matter."
  },
  {
    id: "ledes_1998bi",
    title: "LEDES 1998BI",
    desc: "1998B plus PO_NUMBER, matter and client names, for clients whose e-billing asks for it."
  },
  {
    id: "csv",
    title: "CSV",
    desc: "Fixed, documented columns for spreadsheets and accounting imports."
  },
  {
    id: "xlsx",
    title: "Excel",
    desc: "The CSV columns plus a summary sheet by client and timekeeper."
  }
];

export type Provider = {
  id: string;
  name: string;
  what: string;
  how: string;
  status?: "coming_soon" | "available";
  connected?: boolean;
};

/** Shown until `/time/providers` answers; the server list is the source of truth. */
export const PROVIDERS: ReadonlyArray<Provider> = [
  {
    id: "clio",
    name: "Clio Manage",
    what: "Time entries on matching Clio matters, with UTBMS codes and non-billable flags.",
    how: "Firm admin signs in with Clio. Entries already billed in Clio lock here."
  },
  {
    id: "qbo",
    name: "QuickBooks Online",
    what: "TimeActivity records on the customer or job for each case, with service item and billable status.",
    how: "Invoice from QuickBooks as usual. Billed status syncs back."
  },
  {
    id: "sage_intacct",
    name: "Sage Intacct",
    what: "Weekly timesheets per employee, one line per entry on the matching project and task.",
    how: "Approvals and project invoicing stay in Intacct."
  }
];

export const initials = (name: string): string =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2);

export const formatLabel = (f: ExportFormat): string =>
  EXPORT_FORMATS.find((x) => x.id === f)?.title ?? f;

export const isLedes = (f: ExportFormat): boolean => f.startsWith("ledes");

export const extensionFor = (f: ExportFormat): string =>
  isLedes(f) ? ".txt" : f === "csv" ? ".csv" : ".xlsx";

const SHORT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC"
});

const shortDate = (iso: string) => SHORT.format(new Date(`${iso}T00:00:00Z`));

export const periodLabel = (from?: string, to?: string): string => {
  if (from && to) return `${shortDate(from)} – ${shortDate(to)}`;
  if (from) return `From ${shortDate(from)}`;
  if (to) return `Through ${shortDate(to)}`;
  return "All dates";
};

export const batchPeriod = (
  b: Pick<ExportBatch, "filters" | "work_dates">
): string => {
  const [first, last] = b.work_dates ?? [];
  if (first && first === last) return shortDate(first);
  if (first && last) return periodLabel(first, last);
  return periodLabel(b.filters?.date_from, b.filters?.date_to);
};

export const createdAt = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      });
};

/** Which Billing ID an issue is fixed with, if it can be fixed in place. */
export type IssueFix =
  | {
      field: "matter_no" | "client_no" | "client_matter_id" | "po";
      caseId: string;
    }
  | { field: "employee_client_no"; caseId: string }
  | { field: "timekeeper_id"; userId: string }
  | { field: "classification"; userId: string }
  | { field: "rate"; userId: string }
  | { field: "flat_fee"; caseId: string }
  | { field: "settings" }
  | null;

export const fixFor = (issue: ExportIssue): IssueFix => {
  switch (issue.kind) {
    case "matter_no":
    case "client_no":
    case "client_matter_id":
    case "po":
    case "employee_client_no":
    case "flat_fee":
      return { field: issue.kind, caseId: issue.id };
    case "timekeeper_id":
    case "classification":
    case "rate":
      return { field: issue.kind, userId: issue.id };
    case "firm_ledes_id":
      return { field: "settings" };
    default:
      return null;
  }
};

const TITLES: Record<string, (label: string) => string> = {
  matter_no: (l) => `${l} has no matter number`,
  client_no: (l) => `${l} has no client number`,
  client_matter_id: (l) => `${l} has no client matter ID`,
  po: (l) => `${l} has no PO`,
  employee_client_no: (l) =>
    `${l} has no client number for the employee's share`,
  timekeeper_id: (l) => `${l} has no timekeeper ID`,
  classification: (l) => `${l} has no LEDES classification`,
  rate: (l) => `${l} has no rate`,
  flat_fee: (l) => `${l} has no flat fee amount`,
  firm_ledes_id: () => "Your firm has no LEDES ID"
};

/** Company checks whose message already says whose and what. */
const COMPANY_HINTS: Record<string, string> = {
  no_approved_rate:
    "The client rejects lines from a timekeeper without an approved rate. Record the rate on the company's Billing tab, or leave these out.",
  pending_rate:
    "The rate was submitted but isn't approved yet. Leave these out until it is.",
  above_approved_rate:
    "The client pays the approved rate. Price these lines at it, or adjust the approved rate on the company's Billing tab.",
  ebilling_grouping:
    "Change the company's grouping to one invoice per employee matter on its Billing tab.",
  fee_cap:
    "The file still includes them. Write the time down or check the cap with the client.",
  company_format:
    "The file still exports. Pick that format if the client's system needs it."
};

export const issueTitle = (issue: ExportIssue): string =>
  TITLES[issue.kind]?.(issue.label) ??
  (COMPANY_HINTS[issue.kind]
    ? issue.message
    : `${issue.label}: ${issue.message}`);

export const issueHint = (issue: ExportIssue, format: ExportFormat): string => {
  if (COMPANY_HINTS[issue.kind]) return COMPANY_HINTS[issue.kind];
  if (issue.kind === "narrative")
    return issue.severity === "warn"
      ? "Their description column will be empty. Write one here, or export them as they are."
      : "LEDES needs a description on every line. Write one here, or leave these entries out.";
  if (issue.kind === "flat_fee")
    return "The file carries the hours but no fee line. Add the fee here and it's billed once, the first time the case is exported.";
  if (issue.kind === "employee_client_no")
    return "The employee's share is exported under their own client number. Add it here or leave these entries out.";
  if (issue.severity === "warn") return "The column will be empty in the file.";
  if (issue.kind === "firm_ledes_id")
    return "Usually the firm's tax ID. Add it in Time & billing settings.";
  if (TITLES[issue.kind])
    return isLedes(format)
      ? "Required by LEDES. Add it here or leave these entries out."
      : "Every line needs it so the file can be matched to the case. Add it here or leave these entries out.";
  return issue.message;
};

export const plural = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;
